import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxRootState } from "../../shared/redux/store";
import { ReduxSliceState } from "../../shared/types";
import { OrderTrackingMaintenanceFilter, OrderTrackingMaintenanceItem } from "../../api/types";
import { ReduxTrackingMaintenanceState } from "../types";

const initialState: ReduxTrackingMaintenanceState = {
    pendingState: 'READY',
    pending: [],
    savingOrderIds: [],
};

// Aug 2026 - takes the filter as its payload now (date range + category checkboxes) - the server
// applies all of it, so every filter change re-dispatches this rather than re-filtering an
// already-downloaded list client-side.
export const loadPendingTracking = createAction<OrderTrackingMaintenanceFilter>('trackingMaintenance/loadPendingTracking');
export const saveOrderTracking = createAction<{ orderId: number; returnTrack: string | null }>('trackingMaintenance/saveOrderTracking');

const trackingMaintenanceSlice = createSlice({
    name: 'trackingMaintenance',
    initialState,
    reducers: {
        setPendingState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.pendingState = action.payload;
        },
        setPending: (state, action: PayloadAction<OrderTrackingMaintenanceItem[]>) => {
            state.pending = action.payload;
        },
        setOrderSaving: (state, action: PayloadAction<{ orderId: number; saving: boolean }>) => {
            state.savingOrderIds = action.payload.saving
                ? [...state.savingOrderIds, action.payload.orderId]
                : state.savingOrderIds.filter(id => id !== action.payload.orderId);
        },
        // Applied on a successful save so the row reflects the new value immediately without a
        // full reload - it stays in the list until the next reload even if it no longer qualifies
        // (e.g. tracking # was just filled in), since the underlying kit's own status hasn't
        // necessarily changed yet.
        setOrderManualReturnTrack: (state, action: PayloadAction<{ orderId: number; returnTrack: string | null }>) => {
            const row = state.pending.find(p => p.orderId === action.payload.orderId);
            if (row) row.manualReturnTrack = action.payload.returnTrack;
        },
    },
});

export default trackingMaintenanceSlice;

const selectSelf = (state: ReduxRootState) => state.trackingMaintenance;
export const selectTrackingMaintenanceIsBusy = createSelector(selectSelf, self => self.pendingState === 'BUSY');
export const selectTrackingMaintenancePending = createSelector(selectSelf, self => self.pending);
export const selectTrackingMaintenanceSavingOrderIds = createSelector(selectSelf, self => self.savingOrderIds);
